/**
 * Shared address allowlists for feature gating.
 */
const SHARED_FEATURE_ALLOWLIST = new Set<string>(
  [
    '0x3c420c38b79dc17ede4aba4efcbb5ef9b3a8d997',
    '0x36eac437b566e63ab354c324a12e451c03a34138',
    '0x9f61b30F5607f71AD9E5288C8d9B648Da9076eb4',
    '0x802d5eb9932297775460ba2ba90d339938fff417',
    '0xd508f62817eecdabb0473dfe2e0b1dbb8d9dfab4',
    '0x275dFC57dE99DFC136aE37740Ac5Ce322010Fe07',
    '0x36eac437b566e63ab354c324a12e451c03a34138',
    '0xafbccbfa8016e55f2333d563e6fe2f7002609049',
    '0xd01cdc7a688111b95d68760fe7a2a2423213a5b4',
    '0x4ad64589761633c55b97da0d215a72da1dc78a22',
  ].map((address) => address.toLowerCase()),
);

/**
 * VIP traders eligible for the "Contact VIP Manager" entry.
 * Kept separate from SHARED_FEATURE_ALLOWLIST so VIP gating can evolve
 * independently of other feature flags.
 */
const VIP_FEATURE_ALLOWLIST = new Set<string>(
  [
    '0x9f61b30f5607f71ad9e5288c8d9b648da9076eb4',
    '0x0140aea86f0925b64ef45e18ba7e7226a2e4892b',
    '0xc755de31eba7202f352d6510f704f8e818a21814',
    '0x1952e32f7feeb66e89ca2f2cf5e222220ff651f0',
    '0x474fe6739fa13b01b02f9ccdc5141a6c4fb87fd0',
    '0xfd5a22f753df49bf7fe76aa08c966d69dd85c0b8',
    '0x802d5eb9932297775460ba2ba90d339938fff417',
    '0xfd49fd7bea1e8a4913e17f16a37f0b7a8aedaffc',
    '0xd6d306743d016bcb135e2c9538d0200d09d2ba65',
    '0xfccb9e2ac294c7a6e8b710d9376d7c77c9092a95',
    '0x24762bd395e726fb94a37e3c3e51e7e19a9d7681',
    '0xc806173f17d03fa5175c948a3cd65e3cc2a5e428',
    '0xaebab3d721d8e6427952961fbdc1d0b8b740cd91',
    '0x68f2a8618b80583565a8961bca9b651d00435d9e',
    '0xa883f0e08615ce4783c4a47779125c8a433de8a8',
    '0x5e2fd90c826162ed1b35d2f8212aa878acc770a0',
    '0x36eac437b566e63ab354c324a12e451c03a34138',
    '0x6588472c6819465c65898ce55d2b2f1d15335c39',
    '0x2ac9c3dab14a58ed5c8f79da75cb78573aeee25f',
    '0x2e6d92a2960a4209b6528e73a0e0bf0d208a7da2',
    '0xe23f1ec57c0dbb27ff46b4a176adc6ce9fe78c1c',
    '0x977481cb70efe280c637482aee585670574fc115',
    '0x275dfc57de99dfc136ae37740ac5ce322010fe07',
    '0x01237c62f5c7e9127e4ad28950a7adcc9f9e82cf',
    '0xc245c480262831836e33cc04911dfa0d7d761465',
    '0x53fcbcd2b06a584adad8ef1b75d77cec3c67c518',
    '0x774f43557c38aba64c32bc955e6083b444a56fa1',
    '0x24e61a7781863d1cb6caa6265401e39a850b4552',
    '0x1a6e8e5eb4f8091648c38dbfa336858359499048',
    '0x1ec2749e651c3feee6f7b857d670deed4cd51fc5',
    '0x676af424dfc02f3cb0ea3f62d43cf2ea1ea3c940',
    '0x2a724be1b63caec1c3ef95834fdd443c3347ee1d',
    '0xc30d3df0eb360ac399c8bff8cc7b23e9ee94d1cd',
    '0x824fc14d75610b63689951808cd878f7a7c1129d',
    '0x2d18d3aa2b8e5d2673c75d71cede4afeb784891e',
    '0xe3711d1d1c92b2c8b0444bae6adfa7e632b0ba9e',
    '0xc89a758db95431834bd2153feb27b38703cc144f',
    '0xd432418d21fd239d478cfa2ab5bc7341a45e94c9',
    '0x9193772015cdae3515b50102d7fba7571c7ee208',
    '0xbe6371b58d8bec687932c4877f46a050531d036c',
    '0x1fb8ea0ff3803f0d4e4697ac3bfbb9a95c03eb85',
    '0x56ceddebdac1a0b83fb7ac1c2e28f9f10978eb52',
    '0xb5432287750a0cee46e3562f6a26758eca20fc91',
  ].map((address) => address.toLowerCase()),
);

const normalizeAddress = (address?: string | null): string | null => {
  if (!address) return null;

  return address.toLowerCase();
};

export const isAddressInAllowlist = (
  address: string | null | undefined,
  allowlist: Set<string>,
): boolean => {
  const normalizedAddress = normalizeAddress(address);
  if (!normalizedAddress) return false;

  return allowlist.has(normalizedAddress);
};

export const isAddressInSharedFeatureAllowlist = (
  address?: string | null,
): boolean => {
  return isAddressInAllowlist(address, SHARED_FEATURE_ALLOWLIST);
};

export const isAddressInShowProtocolPNL = (
  address?: string | null,
): boolean => {
  return isAddressInSharedFeatureAllowlist(address);
};

export const isAddressInPrivacyPoolAllowlist = (
  address?: string | null,
): boolean => {
  return isAddressInSharedFeatureAllowlist(address);
};

export const isAddressInFairLaunchAllowlist = (
  address?: string | null,
): boolean => {
  return isAddressInSharedFeatureAllowlist(address);
};

export const isAddressInVipAllowlist = (address?: string | null): boolean => {
  // Internal team (shared feature allowlist) can also see the VIP entry.
  return (
    isAddressInAllowlist(address, VIP_FEATURE_ALLOWLIST) ||
    isAddressInSharedFeatureAllowlist(address)
  );
};
